import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, ArrowRight, ArrowLeft, ChevronUp } from 'lucide-react';
import axios from 'axios';
import VitrineLayout from '../../components/layouts/VitrineLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve an image_url that may be relative (e.g. "/api/static/materials/...") to a full URL.
const resolveImg = (img) => {
  if (!img) return '';
  if (img.startsWith('http://') || img.startsWith('https://')) return img;
  return BACKEND_URL + img;
};

// === Palette (aligned with HomePage) ===
const COPPER       = '#B8895A';
const COPPER_LIGHT = '#D4A878';
const COPPER_DARK  = '#8B5E2F';
const INK          = '#2B1C12';
const CREAM        = '#F5E6D0';
const DARK_BG      = '#120B07';
const DARK_BG_2    = '#1A130D';
const DISPLAY_FF   = "'DM Serif Display', 'Fraunces', 'Cormorant Garamond', serif";

// SVG paper grain
const GRAIN_SVG = encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="220" height="220">' +
    '<filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch"/>' +
    '<feColorMatrix type="saturate" values="0"/></filter>' +
    '<rect width="100%" height="100%" filter="url(#n)" opacity="0.55"/></svg>'
);
const GRAIN_BG = `url("data:image/svg+xml,${GRAIN_SVG}")`;

const fallbackImages = {
  'Granit': 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=900&q=80',
  'Marbre': 'https://images.unsplash.com/photo-1694378060976-66ee61c4f427?w=900&q=80',
  'Quartz': 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=900&q=80',
  'Céramique': 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=900&q=80',
};

function CopperHalo({ x = '50%', y = '50%', size = 700, opacity = 0.10 }) {
  return (
    <div
      aria-hidden
      className="absolute pointer-events-none"
      style={{
        left: x, top: y, width: size, height: size, transform: 'translate(-50%, -50%)',
        background: `radial-gradient(circle, rgba(184,137,90,${opacity}) 0%, rgba(184,137,90,0) 65%)`,
        filter: 'blur(20px)',
      }}
    />
  );
}

// Filter carousel — premium horizontal scroll-snap with chevrons + active highlight
function FilterCarousel({ categories, selected, onSelect, count, query, setQuery }) {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: direction * 320, behavior: 'smooth' });
  };

  const allItems = ['all', ...categories];

  return (
    <div className="flex flex-col gap-5">
      {/* Top: Search + active filter label */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="relative flex items-center gap-3 pb-2 sm:w-72 shrink-0 border-b" style={{ borderColor: 'rgba(245,230,208,0.18)' }}>
          <Search className="w-3.5 h-3.5 shrink-0" style={{ color: COPPER_LIGHT }} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher une matière…"
            className="flex-1 bg-transparent outline-none text-[12px] tracking-[0.05em] placeholder:text-white/30"
            style={{ color: CREAM }}
            data-testid="material-search-input"
          />
          {query && (
            <button onClick={() => setQuery('')} className="shrink-0 opacity-60 hover:opacity-100 transition" aria-label="Effacer">
              <X className="w-3.5 h-3.5" style={{ color: CREAM }} />
            </button>
          )}
        </div>

        {selected !== 'all' && (
          <div className="text-[11px] tracking-[0.18em]" style={{ color: 'rgba(245,230,208,0.6)' }}>
            <span style={{ color: COPPER_LIGHT }}>{count}</span> coloris pour&nbsp;
            <em style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic', color: COPPER_LIGHT, fontSize: '14px' }}>{selected}</em>
          </div>
        )}
      </div>

      {/* Carousel row with chevron buttons */}
      <div className="relative flex items-center gap-3">
        <button
          onClick={() => scroll(-1)}
          aria-label="Précédent"
          className="shrink-0 w-10 h-10 flex items-center justify-center transition-all duration-500 hover:scale-110 backdrop-blur-md"
          style={{
            background: 'rgba(184,137,90,0.10)',
            border: '1px solid rgba(212,168,120,0.40)',
            borderRadius: '2px',
            color: COPPER_LIGHT,
          }}
          data-testid="filter-carousel-prev"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>

        {/* Scroll-snap container */}
        <div
          ref={scrollRef}
          className="flex-1 flex items-center gap-2.5 overflow-x-auto scroll-smooth snap-x snap-mandatory py-1"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          onWheel={(e) => {
            // Translate vertical wheel into horizontal scroll for premium UX
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
              e.currentTarget.scrollBy({ left: e.deltaY, behavior: 'auto' });
            }
          }}
        >
          <style>{`.filter-scroll::-webkit-scrollbar { display: none; }`}</style>
          {allItems.map((cat) => {
            const isAll = cat === 'all';
            const active = selected === cat;
            return (
              <button
                key={cat}
                onClick={() => onSelect(cat)}
                data-testid={`filter-${(isAll ? 'all' : cat.toLowerCase())}`}
                className="group relative snap-start shrink-0 whitespace-nowrap px-6 py-3 text-[11px] uppercase tracking-[0.24em] font-semibold transition-all duration-500 overflow-hidden"
                style={{
                  color: active ? '#FFF8EC' : 'rgba(245,230,208,0.78)',
                  background: active
                    ? 'linear-gradient(135deg, rgba(212,168,120,0.92) 0%, rgba(184,137,90,0.95) 50%, rgba(139,94,47,0.98) 100%)'
                    : 'rgba(255,255,255,0.04)',
                  border: '1px solid ' + (active ? 'rgba(245,230,208,0.55)' : 'rgba(212,168,120,0.30)'),
                  borderRadius: '2px',
                  backdropFilter: 'blur(14px) saturate(140%)',
                  WebkitBackdropFilter: 'blur(14px) saturate(140%)',
                  boxShadow: active
                    ? 'inset 0 1px 0 rgba(255,255,255,0.32), 0 8px 24px -6px rgba(184,137,90,0.55)'
                    : 'inset 0 1px 0 rgba(255,250,240,0.10)',
                  textShadow: active ? 'none' : '0 1px 2px rgba(0,0,0,0.35)',
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.color = COPPER_LIGHT;
                    e.currentTarget.style.borderColor = 'rgba(212,168,120,0.65)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.color = 'rgba(245,230,208,0.78)';
                    e.currentTarget.style.borderColor = 'rgba(212,168,120,0.30)';
                  }
                }}
              >
                {/* Shimmer on hover */}
                <span aria-hidden className="absolute inset-y-0 -left-[120%] w-1/2 group-hover:left-[140%] transition-[left] duration-1000 ease-out pointer-events-none" style={{ background: 'linear-gradient(120deg, transparent 0%, rgba(255,250,240,0.40) 50%, transparent 100%)', transform: 'skewX(-18deg)' }} />
                <span className="relative">{isAll ? 'Tous' : cat}</span>
              </button>
            );
          })}
        </div>

        <button
          onClick={() => scroll(1)}
          aria-label="Suivant"
          className="shrink-0 w-10 h-10 flex items-center justify-center transition-all duration-500 hover:scale-110 backdrop-blur-md"
          style={{
            background: 'rgba(184,137,90,0.10)',
            border: '1px solid rgba(212,168,120,0.40)',
            borderRadius: '2px',
            color: COPPER_LIGHT,
          }}
          data-testid="filter-carousel-next"
        >
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function MaterialsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMaterials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Read ?marque=X on URL change → auto-select brand filter
  useEffect(() => {
    const marque = searchParams.get('marque');
    if (marque) setSelectedCategory(marque);
  }, [searchParams]);

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API_URL}/materials`);
      setMaterials(response.data);
      const uniqueCategories = [...new Set(response.data.map(m => m.category))].sort();
      setCategories(uniqueCategories);
    } catch (error) {
      // Silent fail — UI shows empty state when no materials
    } finally {
      setLoading(false);
    }
  };

  // Update URL when filter changes (deep-linkable)
  const handleSelectCategory = (cat) => {
    setSelectedCategory(cat);
    if (cat === 'all') {
      searchParams.delete('marque');
    } else {
      searchParams.set('marque', cat);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const filteredMaterials = useMemo(() => {
    const base = selectedCategory === 'all'
      ? materials
      : materials.filter(m => m.category === selectedCategory);
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter(m =>
      (m.name || '').toLowerCase().includes(q) ||
      (m.category || '').toLowerCase().includes(q) ||
      (m.description || '').toLowerCase().includes(q)
    );
  }, [materials, selectedCategory, query]);

  return (
    <VitrineLayout autoHideHeader>
      {/* ============ HERO — compact with marble background ============ */}
      <section className="relative pt-24 pb-12 lg:pt-28 lg:pb-16 overflow-hidden" style={{ background: DARK_BG }} data-testid="materials-hero">
        {/* Background marble image — a curated stone macro showing veined materials.
            Existing local asset /hero-granipal.jpg works perfectly here (premium marble macro). */}
        <img
          src="/hero-granipal.jpg"
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            filter: 'brightness(0.45) saturate(0.85) contrast(1.05)',
            objectPosition: 'center 40%',
          }}
        />
        {/* Dual gradient — radial vignette + dark wash for text readability */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(18,11,7,0.45) 0%, rgba(18,11,7,0.78) 75%, rgba(18,11,7,0.95) 100%)',
          }}
        />
        <CopperHalo x="20%" y="30%" size={900} opacity={0.20} />
        <CopperHalo x="85%" y="70%" size={700} opacity={0.14} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.12] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-center gap-4 mb-5">
              <span className="block w-10 h-px" style={{ background: COPPER_LIGHT }} />
              <span className="text-[10px] uppercase tracking-[0.45em]" style={{ color: COPPER_LIGHT }}>Collection</span>
              <span className="block w-10 h-px" style={{ background: COPPER_LIGHT }} />
            </div>
            <h1
              style={{
                fontFamily: DISPLAY_FF,
                fontSize: 'clamp(2.2rem, 5vw, 4rem)',
                fontWeight: 400,
                color: CREAM,
                letterSpacing: '0.03em',
                lineHeight: 1.05,
                textShadow: '0 2px 14px rgba(0,0,0,0.55)',
              }}
            >
              Nos{' '}
              <em style={{ color: COPPER_LIGHT, fontStyle: 'italic', fontWeight: 400, fontFamily: '"Cormorant Garamond", serif' }}>
                matières
              </em>
            </h1>
            <p className="mt-5 text-[14px] lg:text-base font-light max-w-2xl mx-auto leading-relaxed" style={{ color: 'rgba(245,230,208,0.78)', textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}>
              Une sélection rigoureuse des plus beaux matériaux du monde — granit,
              marbre, quartz et céramique haut de gamme.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============ FILTER BAR — sticks at top:0 (header auto-hides), solid opaque when scrolled ============ */}
      <FilterBarSticky
        categories={categories}
        selected={selectedCategory}
        onSelect={handleSelectCategory}
        count={filteredMaterials.length}
        query={query}
        setQuery={setQuery}
      />

      {/* ============ MATERIALS GRID ============ */}
      <section className="relative py-20 lg:py-28 overflow-hidden" style={{ background: DARK_BG }} data-testid="materials-grid">
        <CopperHalo x="50%" y="20%" size={900} opacity={0.06} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.10] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: COPPER, borderTopColor: 'transparent' }} />
            </div>
          ) : filteredMaterials.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-light" style={{ color: 'rgba(245,230,208,0.55)' }}>Aucune matière trouvée pour ce filtre.</p>
              <button
                onClick={() => { handleSelectCategory('all'); setQuery(''); }}
                className="mt-6 px-6 py-2.5 text-[11px] uppercase tracking-[0.3em] rounded-full transition-all duration-500"
                style={{ color: COPPER_LIGHT, border: '1px solid rgba(212,168,120,0.45)' }}
              >
                Réinitialiser
              </button>
            </div>
          ) : (
            <AnimatePresence mode="popLayout">
              <motion.div
                key={selectedCategory + query}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-7"
              >
                {filteredMaterials.map((material, index) => {
                  const hasLink = !!material.supplier_url;
                  const CardWrapper = hasLink ? motion.a : motion.div;
                  const wrapperProps = hasLink
                    ? { href: material.supplier_url, target: '_blank', rel: 'noopener noreferrer' }
                    : {};
                  return (
                  <CardWrapper
                    key={material.id}
                    {...wrapperProps}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(index * 0.025, 0.35), duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="group relative overflow-hidden cursor-pointer block"
                    style={{
                      background: 'linear-gradient(160deg, rgba(245,230,208,0.04) 0%, rgba(184,137,90,0.025) 60%, rgba(20,11,7,0.18) 100%)',
                      border: '1px solid rgba(245,230,208,0.10)',
                      backdropFilter: 'blur(14px)',
                      WebkitBackdropFilter: 'blur(14px)',
                      boxShadow: 'inset 0 1px 0 rgba(245,230,208,0.07), 0 30px 60px -30px rgba(0,0,0,0.55)',
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                    data-testid={`material-card-${material.id}`}
                    title={hasLink ? `Voir ${material.name} chez ${material.category}` : material.name}
                  >
                    {/* Image */}
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={resolveImg(material.image_url) || fallbackImages[material.parent_category] || fallbackImages['Granit']}
                        alt={material.name}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
                        style={{ filter: 'saturate(0.95)' }}
                        onError={(e) => {
                          // Fallback if the local image 404s
                          const fb = fallbackImages[material.parent_category] || fallbackImages['Granit'];
                          if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
                        }}
                      />
                      <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#120B07] via-[#120B07]/15 to-transparent opacity-90" />
                      {/* External link badge */}
                      {hasLink && (
                        <div
                          aria-hidden
                          className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 text-[9px] uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                          style={{
                            background: 'rgba(20,11,7,0.78)',
                            border: '1px solid rgba(212,168,120,0.55)',
                            color: COPPER_LIGHT,
                            backdropFilter: 'blur(8px)',
                          }}
                        >
                          <span>Voir chez {material.category}</span>
                          <ArrowRight className="w-3 h-3" />
                        </div>
                      )}
                      {/* Copper sheen on hover */}
                      <div aria-hidden className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(184,137,90,0.20), transparent 70%)' }} />
                    </div>

                    {/* Body */}
                    <div className="p-5 lg:p-6">
                      <span
                        className="text-[10px] uppercase tracking-[0.32em]"
                        style={{ color: COPPER_LIGHT }}
                      >
                        {material.category}
                      </span>
                      <h3
                        className="mt-1.5"
                        style={{
                          fontFamily: DISPLAY_FF,
                          fontSize: '1.2rem',
                          fontWeight: 400,
                          color: CREAM,
                          letterSpacing: '0.02em',
                          lineHeight: 1.15,
                        }}
                      >
                        {material.name}
                      </h3>
                      {material.description && (
                        <p className="text-[12.5px] mt-2 line-clamp-2 font-light leading-relaxed" style={{ color: 'rgba(245,230,208,0.55)' }}>
                          {material.description}
                        </p>
                      )}
                      <div className="mt-4 flex items-center justify-between">
                        <span aria-hidden className="block h-px transition-all duration-500 group-hover:w-12" style={{ background: COPPER_LIGHT, width: '18px' }} />
                        <span className="text-[10px] uppercase tracking-[0.28em] opacity-60" style={{ color: CREAM }}>
                          {material.parent_category || ''}
                        </span>
                      </div>
                    </div>
                  </CardWrapper>
                );})}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="relative py-24 lg:py-32 overflow-hidden" style={{ background: DARK_BG_2 }} data-testid="materials-cta">
        <CopperHalo x="50%" y="50%" size={1100} opacity={0.16} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.14] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />

        <div className="relative max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <div
            className="relative px-8 py-14 lg:py-20 lg:px-16 overflow-hidden"
            style={{
              background: 'linear-gradient(160deg, rgba(245,230,208,0.04) 0%, rgba(184,137,90,0.025) 60%, rgba(20,11,7,0.18) 100%)',
              border: '1px solid rgba(245,230,208,0.10)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              boxShadow: 'inset 0 1px 0 rgba(245,230,208,0.07), 0 30px 60px -30px rgba(0,0,0,0.6)',
            }}
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="block w-10 h-px" style={{ background: COPPER_LIGHT }} />
              <span className="text-[10px] uppercase tracking-[0.45em]" style={{ color: COPPER_LIGHT }}>Visite atelier</span>
              <span className="block w-10 h-px" style={{ background: COPPER_LIGHT }} />
            </div>
            <h2
              className="mb-6"
              style={{
                fontFamily: DISPLAY_FF,
                fontSize: 'clamp(1.6rem, 3vw, 2.4rem)',
                fontWeight: 400,
                color: CREAM,
                letterSpacing: '0.02em',
                lineHeight: 1.15,
              }}
            >
              Voir nos matières <em style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic', color: COPPER_LIGHT }}>en atelier</em>
            </h2>
            <p className="mb-10 max-w-xl mx-auto font-light leading-relaxed" style={{ color: 'rgba(245,230,208,0.7)' }}>
              Vous souhaitez voir une matière, confirmer une teinte ou comparer plusieurs finitions ?
              Nous vous accueillons à notre atelier pour vous présenter nos échantillons et vous accompagner
              dans le choix de votre pierre.
            </p>
            <Link
              to="/contact"
              className="group relative inline-flex items-center gap-3 px-10 py-4 uppercase text-[11px] tracking-[0.3em] transition-all duration-500 overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(212,168,120,0.85) 0%, rgba(184,137,90,0.92) 45%, rgba(139,94,47,0.95) 100%)',
                color: '#FFF8EC',
                border: '1px solid rgba(245,230,208,0.45)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.28), 0 12px 36px -10px rgba(184,137,90,0.55)',
              }}
              data-testid="materials-contact-cta-btn"
            >
              <span aria-hidden className="absolute inset-y-0 -left-[120%] w-1/2 group-hover:left-[140%] transition-[left] duration-1000 ease-out pointer-events-none" style={{ background: 'linear-gradient(120deg, transparent 0%, rgba(255,250,240,0.55) 50%, transparent 100%)', transform: 'skewX(-18deg)' }} />
              <span className="relative">Nous contacter</span>
              <ArrowRight className="relative w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-500" />
            </Link>
          </div>
        </div>
      </section>

      {/* Back-to-top floating button — appears after scroll */}
      <BackToTopButton />
    </VitrineLayout>
  );
}

// Back-to-top button — visible only when scrolled past 600px
function BackToTopButton() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handler = () => setVisible(window.scrollY > 600);
    handler();
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);
  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          initial={{ opacity: 0, y: 20, scale: 0.85 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.85 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Retour en haut"
          className="fixed bottom-8 right-8 z-40 w-12 h-12 lg:w-14 lg:h-14 flex items-center justify-center backdrop-blur-md transition-all duration-500 hover:scale-110 group"
          style={{
            background: 'linear-gradient(135deg, rgba(212,168,120,0.92) 0%, rgba(184,137,90,0.95) 100%)',
            border: '1px solid rgba(245,230,208,0.45)',
            borderRadius: '2px',
            color: '#FFF8EC',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.22), 0 10px 28px -8px rgba(184,137,90,0.55), 0 0 0 1px rgba(212,168,120,0.20)',
          }}
          data-testid="back-to-top-btn"
        >
          <ChevronUp className="w-5 h-5 group-hover:-translate-y-0.5 transition-transform duration-500" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}


// ============================================================================
// FilterBarSticky — stable opaque bar at top:0 (used when header auto-hides)
// ============================================================================
function FilterBarSticky({ categories, selected, onSelect, count, query, setQuery }) {
  const [solid, setSolid] = useState(false);

  useEffect(() => {
    const handler = () => setSolid(window.scrollY > 60);
    handler();
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Solid opaque dark surface when scrolled; subtle translucent over hero when at top.
  // No backdrop-blur once solid — eliminates flicker on Safari/Chrome.
  const surface = solid
    ? {
        background: '#120B07',
        borderBottom: '1px solid rgba(245,230,208,0.10)',
        boxShadow: '0 12px 32px -16px rgba(0,0,0,0.55)',
      }
    : {
        background: 'rgba(18,11,7,0.62)',
        backdropFilter: 'blur(28px) saturate(140%)',
        WebkitBackdropFilter: 'blur(28px) saturate(140%)',
        borderBottom: '1px solid rgba(245,230,208,0.06)',
      };

  return (
    <section
      className="sticky top-0 z-30 transition-colors duration-300"
      style={surface}
      data-testid="materials-filter-bar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
        <FilterCarousel
          categories={categories}
          selected={selected}
          onSelect={onSelect}
          count={count}
          query={query}
          setQuery={setQuery}
        />
      </div>
    </section>
  );
}
